import { useEffect, useRef, useState } from 'react';
import { AccountTreePicker } from '../../components/ui/AccountTreePicker';
import { Button } from '../../components/ui/button';

// "Save Alternate as Campaign" confirmation (Pre-P2b-3 §5.4). Fires from
// the row editor's edit-mode action bar when the row is using the
// brand's Alternate Campaign — the AM has been customising the
// Alternate's content and wants to materialise the result as a real
// reusable Campaign.
//
// Fields:
//   - Name (required). Default placeholder suggests the source
//     Alternate's name + " (Customised)" so the AM has a starting point;
//     final naming is theirs. Server enforces slug uniqueness per
//     organisational owner — duplicate names get a 400 with the message
//     surfaced inline.
//   - Description (optional, single-line).
//   - Tree picker for shareTargets (defaults to [brand]). Spec §5.4 +
//     Q9: pre-selected target is the rollout's brand only — AM widens
//     or narrows from there.
//
// Default focus is Cancel so a stray Enter doesn't mint a Campaign.

type Props = {
  brandAccountId: string;
  brandName: string;
  alternateCampaignName: string;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: (args: {
    name: string;
    description: string | null;
    shareTargets: Array<{ accountId: string }>;
  }) => void;
};

export function SaveAsCampaignModal({
  brandAccountId,
  brandName,
  alternateCampaignName,
  busy,
  error,
  onCancel,
  onConfirm,
}: Props) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [shareTargetIds, setShareTargetIds] = useState<string[]>([brandAccountId]);

  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onCancel]);

  const trimmedName = name.trim();
  const canSubmit = trimmedName.length > 0 && shareTargetIds.length > 0 && !busy;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-md bg-white shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="save-as-campaign-title"
      >
        <div className="p-6 space-y-4">
          <h2 id="save-as-campaign-title" className="text-lg font-semibold text-slate-900">
            Save this customised Campaign for reuse?
          </h2>
          <p className="text-sm text-slate-600">
            This row's content (Meta ad copy, Google assets, UTM config, landing overrides,
            asset folders, naming) will be saved as a new Campaign. The row will switch from
            using <strong>{alternateCampaignName}</strong> to using the new Campaign.
          </p>

          <div className="space-y-1">
            <label className="text-sm font-medium text-slate-900">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={`${alternateCampaignName} (Customised)`}
              maxLength={200}
              disabled={busy}
            />
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium text-slate-900">Description (optional)</label>
            <input
              type="text"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={5000}
              disabled={busy}
            />
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium text-slate-900">Where can this Campaign be used?</label>
            <p className="text-xs text-slate-500">
              Defaults to the whole brand ({brandName}). Narrow to a specific group or
              location to scope the new Campaign.
            </p>
            <AccountTreePicker
              rootAccountId={brandAccountId}
              value={shareTargetIds}
              onChange={setShareTargetIds}
            />
          </div>

          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              ref={cancelRef}
              type="button"
              onClick={onCancel}
              disabled={busy}
              className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <Button
              type="button"
              disabled={!canSubmit}
              onClick={() =>
                onConfirm({
                  name: trimmedName,
                  description: description.trim() === '' ? null : description.trim(),
                  shareTargets: shareTargetIds.map((accountId) => ({ accountId })),
                })
              }
            >
              {busy ? 'Saving…' : 'Save Campaign'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
